import React from 'react';
import { Card } from '@/components/ui/card';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Mail } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Dynamic Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {/* Header Card */}
        <Card className="mb-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 dark:from-blue-900/20 dark:to-slate-800 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                Privacy Policy
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                FixMyHood
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                Last Updated: 16 February 2026 • POPIA Compliant • Pilot Phase
              </p>
            </div>
          </div>
        </Card>

        {/* Content Card */}
        <Card className="p-8 bg-white dark:bg-slate-800 shadow-lg">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            
            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">1.</span>
                Introduction
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                FixMyHood ("we," "our," "us") is committed to protecting your privacy and complying with the Protection of Personal Information Act, 2013 (POPIA). This Privacy Policy explains how we collect, use, store, disclose, and protect your personal information when you use our estate management platform (the "Service" or "Platform").
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg mb-4">
                <p className="text-slate-900 dark:text-white font-semibold mb-2">Beta Testing Notice</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  FixMyHood is currently in pilot phase. We are not yet a registered (Pty) Ltd company but maintain POPIA compliance standards. Formal incorporation will occur upon product validation.
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
                <p className="text-slate-900 dark:text-white font-semibold mb-2">Our Commitment</p>
                <p className="text-slate-700 dark:text-slate-300">
                  We process personal information lawfully, fairly, and transparently. This policy describes your rights under POPIA and how we safeguard your information.
                </p>
              </div>
              <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <p className="font-semibold text-slate-900 dark:text-white mb-2">Contact Information</p>
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <Mail className="w-4 h-4" />
                  <span>Information Officer: Noah Sparkes</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 mt-1">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:support@fixmyhood.app" className="text-blue-600 dark:text-blue-400 hover:underline">
                    support@fixmyhood.app
                  </a>
                </div>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">2.</span>
                Information We Collect
              </h2>
              
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                2.1 Personal Information You Provide
              </h3>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Account Data:</strong> Full name, email address, phone number (optional), encrypted password</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Profile Information:</strong> Role (Estate Manager, Contractor), estate affiliation, property details, physical address</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Service Usage Data:</strong> Issue reports, photos, GPS location coordinates, comments, maintenance requests, payment information</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                2.2 Information Collected Automatically
              </h3>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Technical Information:</strong> Device type, browser, IP address (for security), usage patterns, cookies</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Location Data:</strong> GPS coordinates for issue reporting and estate boundary management</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                2.3 Information from Third Parties
              </h3>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Google OAuth:</strong> Name, email address, profile picture (if you use Google sign-in)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Payment Processors:</strong> Transaction information from Paystack and Stripe</span>
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">3.</span>
                Legal Basis for Processing
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                We process your personal information based on the following lawful grounds under POPIA:
              </p>
              <div className="grid gap-3">
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white">1. Consent</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">You provide explicit consent when creating an account</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white">2. Contractual Necessity</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Processing is necessary to provide the Service you subscribed to</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white">3. Legal Obligations</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Compliance with South African tax, accounting, and business laws</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white">4. Legitimate Interests</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Fraud prevention, security, service improvement</p>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">4.</span>
                How We Use Your Information
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Primary Service Delivery</h3>
                  <ul className="space-y-1 text-slate-700 dark:text-slate-300 ml-4">
                    <li>• Account management and authentication</li>
                    <li>• Estate management dashboards and analytics</li>
                    <li>• Issue tracking and resolution</li>
                    <li>• Notifications and communication</li>
                    <li>• Payment processing and billing</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Service Improvement</h3>
                  <ul className="space-y-1 text-slate-700 dark:text-slate-300 ml-4">
                    <li>• Analyze usage patterns to improve functionality</li>
                    <li>• Generate anonymized statistics and insights</li>
                    <li>• Research and development for new features</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Security & Compliance</h3>
                  <ul className="space-y-1 text-slate-700 dark:text-slate-300 ml-4">
                    <li>• Detect and prevent fraud, abuse, and security threats</li>
                    <li>• Enforce our Terms of Service</li>
                    <li>• Comply with legal obligations and regulatory requirements</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">5.</span>
                How We Share Your Information
              </h2>
              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg mb-4">
                <p className="text-slate-900 dark:text-white font-semibold">
                  We do not sell, rent, or trade your personal information.
                </p>
              </div>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                We share information only with trusted service providers who assist in operating the Platform:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-100 dark:bg-slate-700">
                    <tr>
                      <th className="px-4 py-2 text-slate-900 dark:text-white">Service</th>
                      <th className="px-4 py-2 text-slate-900 dark:text-white">Purpose</th>
                      <th className="px-4 py-2 text-slate-900 dark:text-white">Location</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700 dark:text-slate-300">
                    <tr className="border-b dark:border-slate-600">
                      <td className="px-4 py-2">Supabase</td>
                      <td className="px-4 py-2">Database, authentication, hosting</td>
                      <td className="px-4 py-2">EU (Stockholm)</td>
                    </tr>
                    <tr className="border-b dark:border-slate-600">
                      <td className="px-4 py-2">Google OAuth</td>
                      <td className="px-4 py-2">Authentication services</td>
                      <td className="px-4 py-2">USA</td>
                    </tr>
                    <tr className="border-b dark:border-slate-600">
                      <td className="px-4 py-2">Paystack</td>
                      <td className="px-4 py-2">Payment processing</td>
                      <td className="px-4 py-2">South Africa</td>
                    </tr>
                    <tr className="border-b dark:border-slate-600">
                      <td className="px-4 py-2">Stripe</td>
                      <td className="px-4 py-2">Payment processing (alternative)</td>
                      <td className="px-4 py-2">USA</td>
                    </tr>
                    <tr className="border-b dark:border-slate-600">
                      <td className="px-4 py-2">Vercel</td>
                      <td className="px-4 py-2">Application hosting</td>
                      <td className="px-4 py-2">USA</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-slate-700 dark:text-slate-300 mt-4 text-sm">
                All service providers are contractually obligated to protect your data. International transfers comply with Chapter 9 of POPIA.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">6.</span>
                Data Security
              </h2>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                We implement industry-standard security measures:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-1">🔒 Encryption</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">TLS/SSL for data transmission</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-1">🔑 Password Security</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Hashed using bcrypt</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-1">🛡️ Access Controls</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Strict internal permissions</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-1">🔍 Security Audits</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Routine vulnerability testing</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-lg">
                <p className="font-semibold text-slate-900 dark:text-white mb-2">Data Breach Notification</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  In the event of a data breach, we will notify you and the Information Regulator within 72 hours as required by POPIA.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">7.</span>
                Data Retention
              </h2>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Active Accounts:</strong> Data retained while your account is active</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Inactive Accounts:</strong> Archived or deleted after 24 months of inactivity</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Deleted Accounts:</strong> Most data deleted within 30 days</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Financial Records:</strong> Retained for 7 years (South African tax law requirement)</span>
                </li>
              </ul>
            </section>

            {/* Section 8 - Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">8.</span>
                Your Rights Under POPIA
              </h2>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-6 rounded-lg mb-4">
                <p className="font-semibold text-slate-900 dark:text-white mb-4">You have the following rights:</p>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">✓ Right to Access</p>
                    <p className="text-slate-700 dark:text-slate-300 text-sm">Request a copy of all personal information we hold about you</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">✓ Right to Correction</p>
                    <p className="text-slate-700 dark:text-slate-300 text-sm">Request correction of inaccurate or incomplete information</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">✓ Right to Deletion</p>
                    <p className="text-slate-700 dark:text-slate-300 text-sm">Request deletion of your account and personal information</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">✓ Right to Object</p>
                    <p className="text-slate-700 dark:text-slate-300 text-sm">Object to processing for marketing purposes</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">✓ Right to Data Portability</p>
                    <p className="text-slate-700 dark:text-slate-300 text-sm">Request your data in a machine-readable format</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">✓ Right to Complain</p>
                    <p className="text-slate-700 dark:text-slate-300 text-sm">Lodge a complaint with the Information Regulator at <a href="https://inforegulator.org.za" className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">inforegulator.org.za</a></p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                  <p className="font-semibold text-slate-900 dark:text-white mb-2">How to Exercise Your Rights</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">
                    Email us at <a href="mailto:support@fixmyhood.app" className="text-blue-600 dark:text-blue-400 hover:underline">support@fixmyhood.app</a>. We will respond within 30 days as required by POPIA.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 9 - Cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">9.</span>
                Cookies and Tracking
              </h2>
              <p className="text-slate-700 dark:text-slate-300 mb-4">We use cookies for:</p>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Essential Cookies:</strong> Required for authentication and core functionality</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Analytics Cookies:</strong> Help us understand platform usage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Preference Cookies:</strong> Remember your settings</span>
                </li>
              </ul>
              <p className="text-slate-700 dark:text-slate-300 mt-4 text-sm">
                We do not use third-party advertising cookies or sell data to advertisers.
              </p>
            </section>

            {/* Section 10 - International Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">10.</span>
                International Data Transfers
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                Some of our service providers are located outside South Africa (EU and USA). When we transfer your personal information internationally, we ensure adequate safeguards are in place (standard contractual clauses, GDPR compliance for EU transfers) and transfers comply with Chapter 9 of POPIA. By using our Service, you consent to these transfers.
              </p>
            </section>

            {/* Section 11 - Changes */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">11.</span>
                Changes to This Policy
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of material changes via email and by updating the "Last Updated" date at the top of this policy. Continued use of the Service after changes constitutes acceptance.
              </p>
            </section>

            {/* Section 12 - Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-blue-600 dark:text-blue-400 mr-2">12.</span>
                Contact Us
              </h2>
              <div className="p-6 bg-gradient-to-br from-blue-50 to-slate-50 dark:from-blue-900/20 dark:to-slate-700/50 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-slate-900 dark:text-white font-semibold mb-3">
                  For questions or to exercise your POPIA rights:
                </p>
                <div className="space-y-2 text-slate-700 dark:text-slate-300">
                  <p><strong>Information Officer:</strong> Noah Sparkes</p>
                  <p><strong>Service:</strong> FixMyHood</p>
                  <p><strong>Email:</strong> <a href="mailto:support@fixmyhood.app" className="text-blue-600 dark:text-blue-400 hover:underline">support@fixmyhood.app</a></p>
                  <p className="text-sm italic">We will respond within 30 days as required by POPIA.</p>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-700">
              <p className="text-center text-slate-600 dark:text-slate-400 text-sm">
                By using FixMyHood, you acknowledge that you have read and understood this Privacy Policy.
              </p>
              <p className="text-center text-slate-500 dark:text-slate-500 text-xs mt-3">
                UI icons by <a href="https://www.flaticon.com/free-icons/ui" target="_blank" rel="noopener noreferrer" className="hover:underline">Iamkikirizky - Flaticon</a>
              </p>
            </div>

          </div>
        </Card>

        {/* Quick Links Footer */}
        <div className="mt-6 flex justify-center gap-4 text-sm">
          <Link
            to="/TermsOfService"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Terms of Service
          </Link>
          {isAuthenticated && (
            <>
              <span className="text-slate-400">•</span>
              <Link
                to="/Settings"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Account Settings
              </Link>
            </>
          )}
          {!isAuthenticated && (
            <>
              <span className="text-slate-400">•</span>
              <Link
                to="/Login"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
